import React, { useEffect, useState } from 'react';
import { apiFetch } from '../constants';
import AuthPageHeader from './AuthPageHeader';
import { tUi } from '../utils/uiText';

export default function SetupAccountPage({ setupToken, onComplete }) {
  const uiLanguage = 'en';
  const [organizationId, setOrganizationId] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [confirmAccessKey, setConfirmAccessKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const validate = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiFetch(
          `/api/auth/setup/validate?token=${encodeURIComponent(setupToken)}`,
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.detail || tUi(uiLanguage, 'setupInvalidOrExpiredLink'));
        }
        setOrganizationId(data.organization_id);
        setOrganizationName(data.organization_name);
      } catch (err) {
        setError(err.message || tUi(uiLanguage, 'setupInvalidOrExpiredLink'));
      } finally {
        setLoading(false);
      }
    };
    if (setupToken) {
      validate();
    } else {
      setLoading(false);
      setError(tUi(uiLanguage, 'setupMissingToken'));
    }
  }, [setupToken]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiFetch('/api/auth/setup/complete', {
        method: 'POST',
        body: JSON.stringify({
          token: setupToken,
          access_key: accessKey,
          confirm_access_key: confirmAccessKey,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || tUi(uiLanguage, 'setupUnableToComplete'));
      }
      setDone(true);
    } catch (err) {
      setError(err.message || tUi(uiLanguage, 'setupUnableToComplete'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-gray-400">{tUi(uiLanguage, 'setupValidatingLink')}</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="w-full max-w-md border border-teal-500/30 rounded-2xl bg-gray-950 p-8 text-center">
          <h1 className="text-2xl font-semibold mb-3">{tUi(uiLanguage, 'setupAccountReadyTitle')}</h1>
          <p className="text-gray-400 text-sm mb-6">
            {tUi(uiLanguage, 'setupAccountReadyBody')}
          </p>
          <button
            type="button"
            onClick={onComplete}
            className="px-4 py-2 bg-teal-500 hover:bg-teal-400 text-black font-semibold rounded-lg"
          >
            {tUi(uiLanguage, 'setupGoToSignIn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
      <div className="w-full max-w-md border border-teal-500/30 rounded-2xl bg-gray-950 p-8 shadow-2xl">
        <AuthPageHeader
          title={tUi(uiLanguage, 'setupSecureAccountTitle')}
          subtitle={
            organizationName
              ? tUi(uiLanguage, 'setupPrivateKeyForOrg', { organization: organizationName })
              : tUi(uiLanguage, 'setupPrivateKeyDefault')
          }
        />

        {error ? (
          <div className="text-sm text-red-400 bg-red-950/40 border border-red-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">{tUi(uiLanguage, 'setupOrganizationId')}</label>
              <input
                value={organizationId}
                readOnly
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-gray-300"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">{tUi(uiLanguage, 'setupChooseAccessKey')}</label>
              <input
                type="password"
                value={accessKey}
                onChange={(e) => setAccessKey(e.target.value)}
                required
                minLength={16}
                className="w-full px-4 py-2 bg-black border border-teal-500/30 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">{tUi(uiLanguage, 'setupAccessKeyRequirements')}</p>
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">{tUi(uiLanguage, 'setupConfirmAccessKey')}</label>
              <input
                type="password"
                value={confirmAccessKey}
                onChange={(e) => setConfirmAccessKey(e.target.value)}
                required
                minLength={16}
                className="w-full px-4 py-2 bg-black border border-teal-500/30 rounded-lg"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 text-black font-semibold rounded-lg"
            >
              {submitting ? tUi(uiLanguage, 'commonSaving') : tUi(uiLanguage, 'setupComplete')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
